import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import UnitSocketContext from './unitSocketContext';
import { useDispatch, useSelector } from 'react-redux';
import {
  UnitSocket,
  Channel,
  handleError,
  getUnitParams,
  getActiveVendors,
} from '@homeplay/api';
import { getGameSlugs, getPromoTexts } from '@homeplay/utils';
import {
  loadUnitFail,
  resetUnitError,
  selectUnitToken,
  setAnonymousUnit,
  setUnitParams,
  updateUnitParams,
} from '../unitSlice';
import {
  removeHappyHourBonusState,
  resetHappyHour,
  setHappyHour,
  setHappyHourBonus,
  setHappyHourConfigs,
  updateHappyHourBonusState,
  setJackpotsLifecycles,
  updateJackpotsLifecycles,
  setJackpotNotification,
  setRecentlyWonJackpots,
} from '../promotionsSlice';
import {
  updateUnitBalance,
  updateUnitBonusCampaignBalance,
} from '../unitSlice';
import { selectUser } from '../../index';
import { useNotification } from '@homeplay/hooks';

const UnitSocketProvider = ({ children, siteName, isTouchUnit }) => {
  const [unitChannel, setUnitChannel] = useState(null);
  const [promotionsChannel, setPromotionsChannel] = useState(null);
  const [jackpotsChannel, setJackpotsChannel] = useState(null);
  const [timeOffset, setTimeOffset] = useState(0);
  const token = useSelector(selectUnitToken);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const { jackpotNotification } = useNotification();
  const location = useLocation();

  useEffect(() => {
    let socket;
    let connectionErrorCount = 0;
    let isCreatingUnit = false;
    if (token) {
      socket = UnitSocket.getInstance(token, true);

      socket.onOpen(() => {
        connectionErrorCount = 0;
        dispatch(resetUnitError());
      });

      socket.onError((err) => {
        if (isCreatingUnit) return;
        getUnitParams(token).catch((error) => {
          if (error.response?.data?.error?.code === 'units_ctx:units:not_found') {
            isCreatingUnit = true;
            const localStorageParamName = siteName ? siteName : 'hp-token';
            localStorage.removeItem(localStorageParamName);
            dispatch(setAnonymousUnit(siteName, isTouchUnit));
          } else {
            connectionErrorCount++;
            if (connectionErrorCount < 3) {
              return;
            }
            dispatch(loadUnitFail({ error: handleError(error, 'reload') }));
          }
        });
      });

      socket.connect();

      if (unitChannel) {
        unitChannel.leave();
        setUnitChannel(null);
      }
      if (promotionsChannel) {
        dispatch(resetHappyHour());
        promotionsChannel.leave();
        setPromotionsChannel(null);
      }
      if (jackpotsChannel) {
        jackpotsChannel.leave();
        setJackpotsChannel(null);
      }

      connectUnitChannel(socket).then((unitChannel) =>
        setUnitChannel(unitChannel)
      );
      connectPromotionsChannel(socket).then((promotionsChannel) =>
        setPromotionsChannel(promotionsChannel)
      );
      connectJackpotsChannel(socket).then((ch) => setJackpotsChannel(ch));
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }

      if (unitChannel) {
        unitChannel.leave();
      }

      if (promotionsChannel) {
        promotionsChannel.leave();
      }
      if (jackpotsChannel) {
        jackpotsChannel.leave();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (promotionsChannel && user) {
      promotionsChannel
        .push('get_hh_bonus_states')
        .then((props) => {
          dispatch(setHappyHourBonus(getHappyHourBonus(props.hh_bonus_states)));
        })
        .catch((error) => console.error(error));

      promotionsChannel.on('hh_bonus_state_updated', (props) => {
        if (props.hh_bonus_state) {
          if (
            props.hh_bonus_state.status === 'ongoing' ||
            props.hh_bonus_state.status === 'complete'
          ) {
            dispatch(
              updateHappyHourBonusState({
                bonusState: getBonusParams(props.hh_bonus_state),
              })
            );
          } else {
            dispatch(
              removeHappyHourBonusState({
                bonusStateId: props.hh_bonus_state.id,
              })
            );
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionsChannel, user]);

  const connectUnitChannel = async (socket) => {
    const channel = new Channel('unit', {
      device_type: isTouchUnit ? 'land_based' : 'online',
    });

    try {
      const { props } = await channel.connect(socket);
      let gameSlugs = getGameSlugs(await getActiveVendors(props.unit?.site?.id));

      //NOTE Crash games and Lotto Assistant are disabled for touchscreen units
      if (isTouchUnit) {
        gameSlugs = gameSlugs.filter(
          (slug) => !['crash-games', 'lns'].includes(slug)
        );
      }

      const { tags_games } = await channel.push('get_tagged_games', {
        slugs: gameSlugs,
      });
      if (tags_games && Object.keys(tags_games).length > 0) {
        props.games = tags_games;
        props.productSlugs = gameSlugs;
        dispatch(setUnitParams(props));
      } else {
        const error = new Error('Something wrong');
        dispatch(loadUnitFail({ error: handleError(error, 'reload') }));
      }
    } catch (error) {
      dispatch(loadUnitFail({ error: handleError(error, 'reload') }));
    }

    channel.on('unit_updated', (props) => {
      switch (props.action) {
        case 'balance_updated':
          dispatch(updateUnitBalance(props.unit.fin_account));
          break;
        case 'active_campaign_bonus_balance_updated':
          dispatch(updateUnitBonusCampaignBalance(props.unit.active_campaign_bonus_balance));
          break;
        default:
          dispatch(updateUnitParams(props));
      }
    });

    if (isTouchUnit) {
      channel.on('command', (props) => {
        if (props.command && props.command === 'shutdown') {
          window.location.replace('https://www.google.com/');
        }
      });
    }

    return channel;
  };

  const connectPromotionsChannel = async (socket) => {
    const channel = new Channel('promotions');
    try {
      const { props } = await channel.connect(socket);
      if (props.promotions) {
        const promotions = props.promotions.filter(
          (promotion) => promotion.type === 'happy_hour'
        );
        const hhPromotions = promotions.map((promotion) =>
          getPromotionConfigs(promotion)
        );
        if (hhPromotions) {
          dispatch(setHappyHourConfigs(hhPromotions));
        }
      }
      if (props.now) {
        setTimeOffset(Date.now() - props.now * 1000);
      }
    } catch (error) {
      console.log('Promotions Channel connect error');
    }

    channel.push('get_ongoing_promotion_states').then((props) => {
      const happyHour = getHappyHourPromotion(props.promotion_states);
      if (happyHour) {
        channel
          .push('get_hh_bonus_state', { promotion_state_id: happyHour.id })
          .then((props) => {
            if (props.hh_bonus_state.status !== 'paid') {
              dispatch(setHappyHour({ happyHour }));
            }
          });
      }
    });

    channel.on('promotion_state_updated', (props) => {
      if (
        props.promotion_state &&
        props.promotion_state.promotion.type === 'happy_hour'
      ) {
        let happyHour = null;
        if (props.promotion_state.status === 'ongoing') {
          happyHour = getHappyHourParams(props.promotion_state);
        }
        dispatch(setHappyHour({ happyHour }));
      }
    });

    return channel;
  };

  const connectJackpotsChannel = async (socket) => {
    const channel = new Channel('jackpots');
    try {
      const { props } = await channel.connect(socket);
      if (props?.jackpot_lifecycles) {
        dispatch(
          setJackpotsLifecycles({
            is_jackpots_enabled: true,
            jackpot_lifecycles: props.jackpot_lifecycles
          })
        );


      } else {
        dispatch(
          setJackpotsLifecycles({
            is_jackpots_enabled: false,
            jackpot_lifecycles: {}
          })
        );
      }
    } catch (error) {
      console.log('Jackpots Channel connect error');
    }

    channel.push('get_lifecycles').then((res) => {
      if (res?.jackpot_lifecycles && Object.keys(res.jackpot_lifecycles).length > 0) {
        dispatch(setJackpotsLifecycles({
            is_jackpots_enabled: true,
          jackpot_lifecycles: res.jackpot_lifecycles
        }));
      } else {
        dispatch(setJackpotsLifecycles({
            is_jackpots_enabled: false,
          jackpot_lifecycles: {}
        }));
      }
    });

    channel.push('get_recently_won_jackpots').then((res) => {
      if (res?.recently_won) {
        dispatch(
          setRecentlyWonJackpots({ recentlyWon: res.recently_won })
        );
      }
    });

    channel.on('jackpot_lifecycle_updated', (payload) => {
      const { action, jackpot_lifecycle, win_lifecycle, is_won } = payload;
      if (action === 'init' || action === 'update') {
        dispatch(updateJackpotsLifecycles({
            action,
          jackpot_lifecycle
        }));
        

      } else if (action === 'stopped' || action === 'error') {
        dispatch(updateJackpotsLifecycles({
            action,
          jackpot_lifecycle
        }));
      } else if (action === 'won' && win_lifecycle) {
        try {
          const isGamePage = location.pathname.includes('/game/');
          jackpotNotification(
            win_lifecycle.slug || win_lifecycle.name,
            win_lifecycle.balance,
            win_lifecycle.winner_name,
            { containerId: isGamePage ? 'game' : undefined }
          );


          // if (is_won) {
          //   dispatch(setJackpotNotification(win_lifecycle));
          // }

        } catch (e) {
          // ignore toast errors
        }

        dispatch(updateJackpotsLifecycles({
            action: 'won',
            win_lifecycle,
          is_won
        }));

        channel.push('get_recently_won_jackpots').then((res) => {
          if (res?.recently_won) {
            dispatch(
              setRecentlyWonJackpots({ recentlyWon: res.recently_won })
            );
          }
        });

        try {
          if (token) {
            getUnitParams(token)
              .then((props) => {
                if (props && props.fin_account) {
                  dispatch(updateUnitBalance(props.fin_account));
                }
              })
              .catch(() => {});
          }
        } catch (_) {}
      }
    });

    return channel;
  };

  const getPromotionConfigs = (promotion) => {
    const hhPromotion = {
      schedule: promotion.schedule,
      duration: promotion.duration * 1000,
      minDeposit: promotion.config.min_deposit,
      bonusPercentage: promotion.config.bonus_percentage,
      maxBonus: promotion.config.max_bonus,
    };
    hhPromotion.texts = getPromoTexts(
      promotion.schedule,
      promotion.duration * 1000
    );

    return hhPromotion;
  };

  const getHappyHourParams = (promotionState) => ({
    id: promotionState.id,
    end: promotionState.end * 1000 + timeOffset,
    minDeposit: promotionState.config.min_deposit,
    bonusPercentage: promotionState.config.bonus_percentage,
  });

  const getBonusParams = (bonusState) => ({
    id: bonusState.id,
    promotionId: bonusState.promotion_state.id,
    complete: bonusState.status === 'complete',
    balance: bonusState.balance,
    amount: bonusState.bonus_amount,
    end: bonusState.end * 1000 + timeOffset,
    isDeposited:
      bonusState.deposit_amount >=
      bonusState.promotion_state.config.min_deposit,
  });

  const getHappyHourPromotion = (promotionStates) => {
    for (let state of promotionStates) {
      if (state.promotion.type === 'happy_hour' && state.status === 'ongoing') {
        return getHappyHourParams(state);
      }
    }
    return null;
  };

  const getHappyHourBonus = (bonusStates) => {
    let hhBonusStates = null;
    let isComplete = false;
    if (bonusStates && bonusStates.length) {
      hhBonusStates = bonusStates.map((state) => {
        if (state.status === 'complete') {
          isComplete = true;
        }
        return getBonusParams(state);
      });
    }
    return { bonusStates: hhBonusStates, isComplete };
  };

  const acquireHappyHourBonus = (stateId) => {
    promotionsChannel.push('acquire_hh_bonus', { hh_bonus_state_id: stateId });
  };

  return (
    <UnitSocketContext.Provider
      value={{ unitChannel, promotionsChannel, jackpotsChannel, acquireHappyHourBonus,  }}
    >
      {children}
    </UnitSocketContext.Provider>
  );
};

export default UnitSocketProvider;
